import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { materialCategoryService } from '../../../services/materialCategoryService';
import materialService from '../../../services/materialService';

const MaterialFormModal = ({ onClose, onSubmit, editingMaterial }) => {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    department: '',
    materialType: '',
    materialCategory: '',
    description: '',
    unitCost: '',
    inventoryLevel: '',
    reorderLevel: '',
    unitOfMeasure: 'unit',
    supplierName: '',
    supplierContact: '',
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Category state - Multi-level cascading
  const [categoryLevels, setCategoryLevels] = useState([
    { level: 0, categories: [], selectedId: null, selectedCategory: null }
  ]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [attributeCategories, setAttributeCategories] = useState([]); // Store attribute categories to display as headings
  const [attributeInputs, setAttributeInputs] = useState({}); // Store text inputs for attributes
  const [existingMaterials, setExistingMaterials] = useState([]); // Store existing materials for SKU duplicate check
  const [generatingSKU, setGeneratingSKU] = useState(false);

  // Fetch Level 0 categories on mount
  useEffect(() => {
    const fetchLevel0Categories = async () => {
      setLoadingCategories(true);
      const { data, error } = await materialCategoryService?.getCategoriesByLevel(0);
      if (!error && data) {
        setCategoryLevels([{
          level: 0,
          categories: data,
          selectedId: null,
          selectedCategory: null
        }]);
      }
      setLoadingCategories(false);
    };
    fetchLevel0Categories();
  }, []);

  // Fetch existing materials for SKU duplicate checking
  useEffect(() => {
    const fetchExistingMaterials = async () => {
      const { data, error } = await materialService?.getAllMaterials();
      if (!error && data) {
        setExistingMaterials(data);
      }
    };
    fetchExistingMaterials();
  }, []);

  useEffect(() => {
    if (editingMaterial) {
      setFormData({
        name: editingMaterial?.name || '',
        sku: editingMaterial?.sku || '',
        department: editingMaterial?.department || '',
        materialType: editingMaterial?.material_type || '',
        materialCategory: editingMaterial?.material_category || '',
        description: editingMaterial?.description || '',
        unitCost: editingMaterial?.unit_cost?.toString() || '',
        inventoryLevel: editingMaterial?.inventory_level?.toString() || '',
        reorderLevel: editingMaterial?.reorder_level?.toString() || '',
        unitOfMeasure: editingMaterial?.unit_of_measure || 'unit',
        supplierName: editingMaterial?.supplier_name || '',
        supplierContact: editingMaterial?.supplier_contact || '',
        status: editingMaterial?.status || 'active'
      });
    }
  }, [editingMaterial]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Extract number prefix from Level 0 category name
  const extractCategoryPrefix = (categoryName) => {
    if (!categoryName) return null;
    // Match patterns like "01", "1", "01 -", "1.", etc. at the start of the name
    const match = categoryName?.match(/^(\d+)/);
    return match ? match?.[1]?.padStart(2, '0') : null; // Ensure 2-digit format
  };

  // Generate unique SKU based on Level 0 category prefix
  const generateUniqueSKU = async (level0Category) => {
    setGeneratingSKU(true);
    
    const prefix = extractCategoryPrefix(level0Category?.name);
    if (!prefix) {
      setGeneratingSKU(false);
      return null;
    }

    // Find all existing SKUs with this prefix
    const existingSKUs = existingMaterials
      ?.map(m => m?.sku)
      ?.filter(sku => sku && sku?.startsWith(prefix))
      ?.filter(Boolean);

    // Extract numeric suffixes and find the highest
    let maxSuffix = 0;
    existingSKUs?.forEach(sku => {
      const suffixMatch = sku?.match(new RegExp(`^${prefix}-(\\d+)$`));
      if (suffixMatch) {
        const suffix = parseInt(suffixMatch?.[1], 10);
        if (suffix > maxSuffix) {
          maxSuffix = suffix;
        }
      }
    });

    // Generate new SKU with next available number
    const newSuffix = (maxSuffix + 1)?.toString()?.padStart(4, '0');
    const newSKU = `${prefix}-${newSuffix}`;
    
    setGeneratingSKU(false);
    return newSKU;
  };

  // Handle cascading category selection
  const handleCategoryChange = async (levelIndex, categoryId) => {
    const selectedCategory = categoryLevels?.[levelIndex]?.categories?.find(cat => cat?.id === categoryId);
    
    if (!selectedCategory) return;

    // Update current level selection
    const updatedLevels = categoryLevels?.slice(0, levelIndex + 1);
    updatedLevels[levelIndex] = {
      ...updatedLevels?.[levelIndex],
      selectedId: categoryId,
      selectedCategory: selectedCategory
    };

    // Build category path for display
    const categoryPath = updatedLevels?.map(lvl => lvl?.selectedCategory?.name)?.filter(Boolean)?.join(' > ');
    setFormData({ ...formData, materialCategory: categoryPath });

    // Auto-generate SKU when Level 0 is selected (only for new materials, not editing)
    if (levelIndex === 0 && !editingMaterial) {
      const generatedSKU = await generateUniqueSKU(selectedCategory);
      if (generatedSKU) {
        setFormData(prev => ({ ...prev, sku: generatedSKU }));
      }
    }

    // Check if selected category has material_attributes ticked
    if (selectedCategory?.material_attributes) {
      // Display ALL categories at this level as attribute headings
      const currentLevelCategories = categoryLevels?.[levelIndex]?.categories || [];
      setAttributeCategories(currentLevelCategories);
      
      setCategoryLevels(updatedLevels);
      if (errors?.materialCategory) {
        setErrors({ ...errors, materialCategory: '' });
      }
      return;
    }

    // Clear attribute categories if not material_attributes
    setAttributeCategories([]);

    // Fetch child categories for next level
    const { data: childCategories, error } = await materialCategoryService?.getCategoriesByParent(categoryId);
    
    if (!error && childCategories && childCategories?.length > 0) {
      // Check if any child has material_attributes enabled
      const hasAttributeEnabled = childCategories?.some(cat => cat?.material_attributes);
      
      if (hasAttributeEnabled) {
        // Display ALL child categories as attribute headings
        setAttributeCategories(childCategories);
        setCategoryLevels(updatedLevels);
        if (errors?.materialCategory) {
          setErrors({ ...errors, materialCategory: '' });
        }
        return;
      }
      
      // Add next level for further selection
      updatedLevels?.push({
        level: selectedCategory?.level + 1,
        categories: childCategories,
        selectedId: null,
        selectedCategory: null
      });
    }

    setCategoryLevels(updatedLevels);
    if (errors?.materialCategory) {
      setErrors({ ...errors, materialCategory: '' });
    }
  };

  // Handle attribute text input
  const handleAttributeInput = (categoryId, value) => {
    setAttributeInputs({
      ...attributeInputs,
      [categoryId]: value
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Material name is required';
    if (!formData?.department?.trim()) newErrors.department = 'Department is required';
    if (!formData?.materialType?.trim()) newErrors.materialType = 'Material type is required';
    if (!formData?.materialCategory?.trim()) newErrors.materialCategory = 'Category is required';
    if (formData?.unitCost && isNaN(parseFloat(formData?.unitCost))) {
      newErrors.unitCost = 'Unit cost must be a valid number';
    }
    if (formData?.inventoryLevel && isNaN(parseInt(formData?.inventoryLevel))) {
      newErrors.inventoryLevel = 'Inventory level must be a valid number';
    }
    if (formData?.reorderLevel && isNaN(parseInt(formData?.reorderLevel))) {
      newErrors.reorderLevel = 'Reorder level must be a valid number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    const submitData = {
      ...formData,
      unitCost: formData?.unitCost ? parseFloat(formData?.unitCost) : 0,
      inventoryLevel: formData?.inventoryLevel ? parseInt(formData?.inventoryLevel) : 0,
      reorderLevel: formData?.reorderLevel ? parseInt(formData?.reorderLevel) : 0,
      attributeInputs: attributeInputs // Include attribute inputs
    };
    await onSubmit(submitData);
    setSubmitting(false);
  };

  const departmentOptions = [
    { value: '', label: 'Select Department' },
    { value: 'Production', label: 'Production' },
    { value: 'Quality', label: 'Quality' },
    { value: 'Maintenance', label: 'Maintenance' },
    { value: 'Warehouse', label: 'Warehouse' }
  ];

  const materialTypeOptions = [
    { value: '', label: 'Select Material Type' },
    { value: 'Raw Materials', label: 'Raw Materials' },
    { value: 'Components', label: 'Components' },
    { value: 'Consumables', label: 'Consumables' },
    { value: 'Finished Goods', label: 'Finished Goods' }
  ];

  const unitOfMeasureOptions = [
    { value: 'unit', label: 'Unit' },
    { value: 'piece', label: 'Piece' },
    { value: 'meter', label: 'Meter' },
    { value: 'kg', label: 'Kilogram' },
    { value: 'liter', label: 'Liter' },
    { value: 'sheet', label: 'Sheet' },
    { value: 'box', label: 'Box' },
    { value: 'roll', label: 'Roll' },
    { value: 'cartridge', label: 'Cartridge' },
    { value: 'cubic meter', label: 'Cubic Meter' },
    { value: 'square meter', label: 'Square Meter' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'discontinued', label: 'Discontinued' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={editingMaterial ? "Edit" : "Plus"} size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {editingMaterial ? 'Edit Material' : 'Create New Material'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {editingMaterial ? 'Update material information' : 'Add a new material to the library'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Material Name"
                  placeholder="Enter material name"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  error={errors?.name}
                  required
                />
                <Input
                  label="SKU"
                  placeholder="Enter SKU code"
                  value={formData?.sku}
                  onChange={(e) => handleInputChange('sku', e?.target?.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Select
                  label="Department"
                  options={departmentOptions}
                  value={formData?.department}
                  onChange={(value) => handleInputChange('department', value)}
                  error={errors?.department}
                  required
                />
                <Select
                  label="Material Type"
                  options={materialTypeOptions}
                  value={formData?.materialType}
                  onChange={(value) => handleInputChange('materialType', value)}
                  error={errors?.materialType}
                  required
                />
              </div>
              
              {/* Cascading Category Dropdowns */}
              <div className="mt-4 space-y-4">
                <label className="text-sm font-medium text-foreground">Material Category *</label>
                
                {categoryLevels?.map((levelData, index) => (
                  <div key={`level-${levelData?.level}`} className="space-y-2">
                    <Select
                      label={`Level ${levelData?.level}`}
                      options={[
                        { value: '', label: loadingCategories ? 'Loading...' : `Select Level ${levelData?.level}` },
                        ...levelData?.categories?.map(cat => ({
                          value: cat?.id,
                          label: cat?.name
                        }))
                      ]}
                      value={levelData?.selectedId || ''}
                      onChange={(value) => handleCategoryChange(index, value)}
                      disabled={loadingCategories}
                    />
                  </div>
                ))}
                
                {/* Show attribute categories as headings with text inputs when material_attributes is ticked */}
                {attributeCategories?.length > 0 && (
                  <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="FileText" size={18} color="var(--color-primary)" />
                      <span className="text-base font-semibold text-foreground">
                        Material Attributes
                      </span>
                    </div>
                    {attributeCategories?.map((attrCategory) => (
                      <div key={attrCategory?.id} className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          {attrCategory?.name}
                        </label>
                        <Input
                          placeholder={`Enter ${attrCategory?.name?.toLowerCase()}`}
                          value={attributeInputs?.[attrCategory?.id] || ''}
                          onChange={(e) => handleAttributeInput(attrCategory?.id, e?.target?.value)}
                        />
                      </div>
                    ))}
                  </div>
                )}
                
                {errors?.materialCategory && (
                  <p className="text-sm text-destructive mt-1">{errors?.materialCategory}</p>
                )}
                
                {/* Display selected category path */}
                {formData?.materialCategory && (
                  <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <Icon name="Layers" size={16} color="var(--color-primary)" />
                    <span className="text-sm text-foreground font-medium">
                      Selected: {formData?.materialCategory}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => handleInputChange('status', value)}
                />
              </div>
              <div className="mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Description</label>
                  <textarea
                    placeholder="Enter material description"
                    value={formData?.description}
                    onChange={(e) => handleInputChange('description', e?.target?.value)}
                    className="w-full h-20 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            </div>

            {/* Cost and Inventory */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Cost & Inventory</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Unit Cost"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData?.unitCost}
                  onChange={(e) => handleInputChange('unitCost', e?.target?.value)}
                  error={errors?.unitCost}
                />
                <Select
                  label="Unit of Measure"
                  options={unitOfMeasureOptions}
                  value={formData?.unitOfMeasure}
                  onChange={(value) => handleInputChange('unitOfMeasure', value)}
                />
                <Input
                  label="Current Inventory Level"
                  type="number"
                  placeholder="0"
                  value={formData?.inventoryLevel}
                  onChange={(e) => handleInputChange('inventoryLevel', e?.target?.value)}
                  error={errors?.inventoryLevel}
                />
                <Input
                  label="Reorder Level"
                  type="number"
                  placeholder="0"
                  value={formData?.reorderLevel}
                  onChange={(e) => handleInputChange('reorderLevel', e?.target?.value)}
                  error={errors?.reorderLevel}
                  description="Minimum stock level before reorder"
                />
              </div>
            </div>

            {/* Supplier Information */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Supplier Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Supplier Name"
                  placeholder="Enter supplier name"
                  value={formData?.supplierName}
                  onChange={(e) => handleInputChange('supplierName', e?.target?.value)}
                />
                <Input
                  label="Supplier Contact"
                  placeholder="Email or phone"
                  value={formData?.supplierContact}
                  onChange={(e) => handleInputChange('supplierContact', e?.target?.value)}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {editingMaterial ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Icon name={editingMaterial ? "Save" : "Plus"} size={18} className="mr-2" />
                {editingMaterial ? 'Update Material' : 'Create Material'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MaterialFormModal;